import { useState, useEffect, useMemo } from "react";
import { listCapabilities, createCapability, updateCapability, deleteCapability } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Plus, Pencil, Trash2, Lock, Info } from "lucide-react";

const SUPPORT = ["supported", "limited", "not_supported"];
const SUPPORT_LABELS = { supported: "Supported", limited: "Limited", not_supported: "Not Supported" };
const SUPPORT_STYLES = {
  supported: "bg-[#E0F2FE] text-[#025979] border-[#025979]/20",
  limited: "bg-amber-50 text-amber-700 border-amber-200",
  not_supported: "bg-gray-50 text-[#9CA3AF] border-gray-200",
};

const SECTIONS = [
  "Core Warehouse Execution",
  "Process Optimization & Control",
  "Advanced Operations",
  "Automation & Yard",
  "Platform & Integration",
];

const GROUPS = [
  { value: "core", label: "Core" },
  { value: "optimization", label: "Optimization" },
  { value: "advanced", label: "Advanced" },
  { value: "automation", label: "Automation" },
  { value: "platform", label: "Platform" },
];

const SECTION_TO_GROUP = {
  "Core Warehouse Execution": "core",
  "Process Optimization & Control": "optimization",
  "Advanced Operations": "advanced",
  "Automation & Yard": "automation",
  "Platform & Integration": "platform",
};

const SECTION_TO_WEIGHT = {
  "Core Warehouse Execution": 3,
  "Process Optimization & Control": 4,
  "Advanced Operations": 4,
  "Automation & Yard": 5,
  "Platform & Integration": 3,
};

const EMPTY = {
  capability_name: "", section: SECTIONS[0], description: "",
  strm_support: "not_supported", ewm_basic_support: "not_supported",
  ewm_advanced_support: "not_supported", lgm_support: "not_supported",
  display_order: 0, active: true, protected: false,
  importance_weight: 4, capability_group: "core",
};

function SupportBadge({ value }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-medium border ${SUPPORT_STYLES[value] || SUPPORT_STYLES.not_supported}`}>
      {SUPPORT_LABELS[value] || value}
    </span>
  );
}

function SupportSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm text-[10px] h-8 w-full"><SelectValue /></SelectTrigger>
      <SelectContent>{SUPPORT.map(s => <SelectItem key={s} value={s}>{SUPPORT_LABELS[s]}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const WEIGHT_META = {
  5: { color: "#025979", label: "Weight 5 — Critical decision driver" },
  4: { color: "#0891B2", label: "Weight 4 — High impact capability" },
  3: { color: "#14B8A6", label: "Weight 3 — Moderate supporting capability" },
  2: { color: "#94A3B8", label: "Weight 2 — Basic / contextual capability" },
  1: { color: "#CBD5E1", label: "Weight 1 — Optional / low impact capability" },
};

function WeightDots({ weight }) {
  const w = Math.min(5, Math.max(1, weight || 3));
  const meta = WEIGHT_META[w];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex gap-0.5 items-center justify-center cursor-help"
          data-testid={`weight-dots-${w}`}
          aria-label={meta.label}
        >
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{ backgroundColor: i <= w ? meta.color : "#E5E7EB" }}
            />
          ))}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top"><p className="text-xs">{meta.label}</p></TooltipContent>
    </Tooltip>
  );
}

export default function CapabilityMatrixPage() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");

  const load = () => listCapabilities().then(d => setItems(d.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)))).catch(() => {});
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    SECTIONS.forEach(s => { g[s] = []; });
    items.forEach(item => {
      const sec = item.section || "Core Warehouse Execution";
      if (!g[sec]) g[sec] = [];
      g[sec].push(item);
    });
    return g;
  }, [items]);

  const showMsg = (text, type = "success") => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(""), 3000);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, display_order: items.length });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({ ...EMPTY, ...item });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.capability_name.trim()) return;
    if (form.importance_weight < 1 || form.importance_weight > 5) {
      showMsg("Importance weight must be between 1 and 5", "error"); return;
    }
    try {
      if (editing) await updateCapability(editing, form);
      else await createCapability(form);
      setShowModal(false); setEditing(null); setForm({ ...EMPTY });
      showMsg("Capability saved"); load();
    } catch { showMsg("Error saving", "error"); }
  };

  const attemptDelete = (item) => {
    if (item.protected) {
      showMsg("This capability is protected and cannot be deleted.", "error");
      return;
    }
    setDeleteTarget(item.id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCapability(deleteTarget);
      setDeleteTarget(null);
      showMsg("Capability deleted"); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      showMsg(typeof detail === "string" ? detail : "Error deleting", "error");
      setDeleteTarget(null);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>SAP Capability Matrix</h1>
          <Button data-testid="add-capability-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Capability
          </Button>
        </div>
        <p className="text-xs text-[#9CA3AF] mb-5">Consulting-grade capability comparison across SAP warehouse solutions. Protected capabilities cannot be deleted.</p>

        {msg && (
          <div className={`mb-3 px-3 py-2 rounded-sm text-xs font-medium border ${
            msgType === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-[#E0F2FE] border-[#025979]/20 text-[#025979]"
          }`}>{msg}</div>
        )}

        {/* Grouped sections */}
        <div className="space-y-5">
          {SECTIONS.map(sectionName => {
            const sectionItems = grouped[sectionName] || [];
            if (sectionItems.length === 0) return null;
            return (
              <div key={sectionName}>
                <h2 className="text-sm font-semibold text-[#111827] mb-2 px-1" style={{ fontFamily: "Chivo" }}>{sectionName}</h2>
                <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E5E7EB]">
                        <th className="text-left px-4 py-2 text-[#6B7280] font-medium">Capability</th>
                        <th className="text-center px-1.5 py-2 text-[#6B7280] font-medium w-10">Wt</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-24">StRM</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-24">EWM Basic</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-24">EWM Adv</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-24">LGM</th>
                        <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionItems.map(item => (
                        <tr key={item.id} className="border-b border-[#E5E7EB] last:border-0 hover:bg-gray-50/50 transition-colors" data-testid={`cap-row-${item.id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              {item.protected && (
                                <Tooltip>
                                  <TooltipTrigger asChild><Lock className="w-3 h-3 text-[#025979] shrink-0" /></TooltipTrigger>
                                  <TooltipContent side="top"><p className="text-xs">Protected capability</p></TooltipContent>
                                </Tooltip>
                              )}
                              <span className="text-[#111827] font-medium">{item.capability_name}</span>
                              {item.description && (
                                <Tooltip>
                                  <TooltipTrigger asChild><Info className="w-3 h-3 text-[#9CA3AF] shrink-0 cursor-help" /></TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs"><p className="text-xs">{item.description}</p></TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="px-1.5 py-2.5 text-center"><WeightDots weight={item.importance_weight || 3} /></td>
                          <td className="px-2 py-2.5 text-center"><SupportBadge value={item.strm_support} /></td>
                          <td className="px-2 py-2.5 text-center"><SupportBadge value={item.ewm_basic_support} /></td>
                          <td className="px-2 py-2.5 text-center"><SupportBadge value={item.ewm_advanced_support} /></td>
                          <td className="px-2 py-2.5 text-center"><SupportBadge value={item.lgm_support} /></td>
                          <td className="px-3 py-2.5 text-right">
                            <button onClick={() => openEdit(item)} className="text-[#025979] hover:text-[#03738C] mr-2 transition-colors" title="Edit">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {item.protected ? (
                              <span className="text-[#E5E7EB] cursor-not-allowed" title="Protected"><Trash2 className="w-3.5 h-3.5 inline" /></span>
                            ) : (
                              <button onClick={() => attemptDelete(item)} className="text-red-400 hover:text-red-600 transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); setEditing(null); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Capability" : "Add Capability"}</DialogTitle>
              <DialogDescription>Define capability details and support levels per SAP solution.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-xs font-medium text-[#374151] mb-1.5 block">Capability Name <span className="text-red-500">*</span></label>
                <Input data-testid="cap-name-input" value={form.capability_name} onChange={e => setForm({ ...form, capability_name: e.target.value })} className="rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#374151] mb-1.5 block">Description</label>
                <Textarea data-testid="cap-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm text-xs" rows={2} placeholder="Brief description of this capability" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#374151] mb-1.5 block">Section</label>
                  <Select value={form.section} onValueChange={v => setForm({ ...form, section: v, capability_group: SECTION_TO_GROUP[v] || "core", importance_weight: SECTION_TO_WEIGHT[v] || 3 })}>
                    <SelectTrigger data-testid="cap-section-select" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#374151] mb-1.5 block">Capability Group</label>
                  <Select value={form.capability_group} onValueChange={v => setForm({ ...form, capability_group: v })}>
                    <SelectTrigger className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{GROUPS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#374151] mb-1.5 block">Importance Weight (1–5)</label>
                  <Input data-testid="cap-weight-input" type="number" min={1} max={5} value={form.importance_weight} onChange={e => setForm({ ...form, importance_weight: Math.min(5, Math.max(1, parseInt(e.target.value) || 3)) })} className="rounded-sm text-xs" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.protected} onChange={e => setForm({ ...form, protected: e.target.checked })} className="rounded-sm" />
                    Protected (prevent deletion)
                  </label>
                </div>
              </div>
              <div className="border-t border-[#E5E7EB] pt-3">
                <p className="text-xs font-medium text-[#374151] mb-2">Support Levels</p>
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="text-[10px] text-[#6B7280] mb-1 block">StRM</label><SupportSelect value={form.strm_support} onChange={v => setForm({ ...form, strm_support: v })} /></div>
                  <div><label className="text-[10px] text-[#6B7280] mb-1 block">EWM Basic</label><SupportSelect value={form.ewm_basic_support} onChange={v => setForm({ ...form, ewm_basic_support: v })} /></div>
                  <div><label className="text-[10px] text-[#6B7280] mb-1 block">EWM Advanced</label><SupportSelect value={form.ewm_advanced_support} onChange={v => setForm({ ...form, ewm_advanced_support: v })} /></div>
                  <div><label className="text-[10px] text-[#6B7280] mb-1 block">LGM</label><SupportSelect value={form.lgm_support} onChange={v => setForm({ ...form, lgm_support: v })} /></div>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditing(null); }} className="rounded-sm text-xs">Cancel</Button>
              <Button data-testid="cap-save-btn" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
                {editing ? "Update" : "Add Capability"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Capability</DialogTitle>
              <DialogDescription>Are you sure you want to delete this capability? This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
              <Button data-testid="cap-confirm-delete" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
